/**
 * Saved progress for the RFQ Create Wizard — CR-5461, hardened under CR-6207.
 *
 * One draft per user, stored server-side as opaque JSON (GET / PUT / DELETE
 * /rfq/drafts/me/). Line items ride in the same draft as the header, costs,
 * certificates and suppliers. This hook owns everything about that draft so
 * RFQCreateWizard stays a view component: loading it, the resume / discard
 * choice, debounced auto-save, the leave-page guard, saving on close, and
 * clearing it once the RFQ is created.
 *
 * CR-6207 rules:
 *  - Nothing is saved until the user changes something. Opening the wizard
 *    used to save an empty draft 3s later, which raised a false "Resume where
 *    you left off?" prompt and list-page banner, and made "Discard & start
 *    fresh" undo itself.
 *  - Edits not yet saved are never dropped silently. Closing the wizard saves
 *    them first, leaving it in-app saves them on the way out, a browser
 *    refresh / tab close asks first, and a failed save retries on its own.
 *  - Saves run one at a time, in order, so an older save can never land after
 *    a newer one — or after the draft is deleted on submit.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rfqAPI } from '../../lib/api';
import { useAutoSave } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { toast } from 'sonner';
import {
  STEPS,
  DEFAULT_BASIC_DETAILS,
  EMPTY_LINE_ITEM,
  EMPTY_COST,
  EMPTY_CERTIFICATE,
} from './rfqWizardTypes';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
} from './rfqWizardTypes';

/** Quiet time after the last edit before it is saved. */
const AUTO_SAVE_DELAY_MS = 3000;
/** Wait before retrying a save that failed. */
const RETRY_DELAY_MS = 10000;

/** The wizard state persisted as the draft. Validation happens at submit. */
export interface RFQWizardDraftState {
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  additionalCosts: AdditionalCostForm[];
  requiredCertificates: RequiredCertificateForm[];
  selectedSuppliers: number[];
  currentStep: number;
}

/**
 * Assemble the draft in one fixed key order.
 *
 * Unsaved changes are detected by comparing JSON strings, so the wizard's live
 * state and a hydrated draft must both be built here or identical content
 * could compare as different.
 *
 * @param parts - the six pieces of wizard state
 * @returns the draft object
 */
export function buildWizardDraftState(parts: RFQWizardDraftState): RFQWizardDraftState {
  return {
    basicDetails: parts.basicDetails,
    lineItems: parts.lineItems,
    additionalCosts: parts.additionalCosts,
    requiredCertificates: parts.requiredCertificates,
    selectedSuppliers: parts.selectedSuppliers,
    currentStep: parts.currentStep,
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Lay a stored row over its empty template so fields added since it was saved get their defaults. */
const overlay = <T extends object>(row: Record<string, unknown>, template: T): T =>
  ({ ...template, ...row }) as T;

/** Rows of a stored list, each laid over the template; null when the list is missing or not a list. */
function overlayRows<T extends object>(rows: unknown, template: T): T[] | null {
  if (!Array.isArray(rows)) return null;
  return rows.filter(isRecord).map((row) => overlay(row, template));
}

/**
 * Turn a stored draft back into complete wizard state.
 *
 * A draft can predate fields added to the wizard since (one saved before
 * CR-5467 has no drawing_issue on its lines), so every row is laid over its
 * empty template and anything missing or malformed falls back to `fallback`.
 *
 * @param stored - wizard_state as returned by GET /rfq/drafts/me/
 * @param fallback - the wizard's opening state
 * @returns state the wizard can render and submit
 */
export function hydrateWizardDraft(stored: unknown, fallback: RFQWizardDraftState): RFQWizardDraftState {
  const s = isRecord(stored) ? stored : {};
  const basicDetails = s['basicDetails'];
  const lineItems = overlayRows(s['lineItems'], EMPTY_LINE_ITEM);
  const suppliers = s['selectedSuppliers'];
  const step = s['currentStep'];
  return buildWizardDraftState({
    basicDetails: isRecord(basicDetails) ? overlay(basicDetails, DEFAULT_BASIC_DETAILS) : fallback.basicDetails,
    // The Line Items step always shows at least one line.
    lineItems: lineItems && lineItems.length > 0 ? lineItems : fallback.lineItems,
    additionalCosts: overlayRows(s['additionalCosts'], EMPTY_COST) ?? fallback.additionalCosts,
    requiredCertificates: overlayRows(s['requiredCertificates'], EMPTY_CERTIFICATE) ?? fallback.requiredCertificates,
    selectedSuppliers: Array.isArray(suppliers)
      ? suppliers.filter((id): id is number => typeof id === 'number')
      : fallback.selectedSuppliers,
    currentStep: typeof step === 'number' ? Math.max(1, Math.min(STEPS.length, Math.trunc(step))) : fallback.currentStep,
  });
}

/** The wizard as it opens: blank details, one blank line, step 1. */
export function openingWizardDraftState(): RFQWizardDraftState {
  return buildWizardDraftState({
    basicDetails: { ...DEFAULT_BASIC_DETAILS },
    lineItems: [{ ...EMPTY_LINE_ITEM }],
    additionalCosts: [],
    requiredCertificates: [],
    selectedSuppliers: [],
    currentStep: 1,
  });
}

/**
 * Whether a stored draft is just the blank wizard.
 *
 * Before CR-6207 the wizard saved its blank form 3s after opening, so users
 * who opened Create RFQ and left without typing were left with an empty draft
 * that raised a "Resume" prompt and the list-page banner. Such a draft holds
 * nothing to resume and is treated as no draft.
 *
 * @param stored - wizard_state as returned by GET /rfq/drafts/me/
 * @returns true when resuming it would only reproduce the blank form
 */
export function isUntouchedDraft(stored: unknown): boolean {
  const opening = openingWizardDraftState();
  return JSON.stringify(hydrateWizardDraft(stored, opening)) === JSON.stringify(opening);
}

interface UseRfqWizardDraftParams {
  /** Live wizard state, built with buildWizardDraftState. */
  wizardState: RFQWizardDraftState;
  /** True while the RFQ is being created — auto-save pauses. */
  submitting: boolean;
  /** Apply a resumed draft to the wizard's state. */
  onHydrate: (state: RFQWizardDraftState) => void;
}

export interface RfqWizardDraft {
  /** A saved draft is waiting for the user to resume or discard it. */
  pendingResume: { updatedAt: string } | null;
  resume: () => void;
  discard: () => Promise<void>;
  /** Everything the save-status pill needs. */
  saveStatus: {
    visible: boolean;
    status: AutoSaveStatus;
    lastSavedAt: Date | null;
    hasUnsavedChanges: boolean;
  };
  /** True while closing waits for the last edits to save. */
  closing: boolean;
  /**
   * Save outstanding edits before the wizard closes.
   * @returns true when it is safe to close; false (after a toast) when the save failed.
   */
  saveBeforeClose: () => Promise<boolean>;
  /** Stop saving and delete the draft once the RFQ has been created. */
  clearAfterSubmit: () => Promise<void>;
}

/**
 * Server-side draft for the RFQ Create Wizard.
 *
 * @param params - live wizard state, whether a submit is running, and how to
 *   apply a resumed draft
 * @returns the resume prompt state and actions, the save-status pill state,
 *   and the close / submit hooks
 * @link RFQCreateWizard
 */
export function useRfqWizardDraft({ wizardState, submitting, onHydrate }: UseRfqWizardDraftParams): RfqWizardDraft {
  const [draftLoading, setDraftLoading] = useState(true);
  const [pending, setPending] = useState<{ state: unknown; updatedAt: string } | null>(null);
  const [resumedFrom, setResumedFrom] = useState<Date | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [closing, setClosing] = useState(false);
  const openingStateRef = useRef(wizardState);

  const currentJson = useMemo(() => JSON.stringify(wizardState), [wizardState]);
  // What the server holds — or, until the first save, the untouched form, so
  // an unedited wizard has nothing to save.
  const [savedJson, setSavedJson] = useState(currentJson);
  const hasUnsavedChanges = currentJson !== savedJson;
  const draftReady = !draftLoading && pending === null;

  useEffect(() => {
    let cancelled = false;
    rfqAPI.getDraft()
      .then((response) => {
        if (cancelled || response.status === 204 || !response.data) return;
        if (isUntouchedDraft(response.data.wizard_state)) {
          // A blank draft left by the pre-CR-6207 wizard — nothing to resume.
          rfqAPI.deleteDraft().catch((error: unknown) => {
            console.warn('[CR-6207] failed to delete blank RFQ draft', error);
          });
          return;
        }
        setPending({ state: response.data.wizard_state, updatedAt: response.data.updated_at });
      })
      .catch((error: unknown) => {
        // Don't block the wizard — the user can keep working and auto-save
        // will create the draft once they edit.
        console.warn('[CR-5461] failed to fetch RFQ draft', error);
      })
      .finally(() => {
        if (!cancelled) setDraftLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Each save waits for the one before it. The chain itself never rejects,
  // so one failed save doesn't poison the next.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const saveDraft = useCallback((state: RFQWizardDraftState): Promise<void> => {
    const json = JSON.stringify(state);
    const attempt = saveChainRef.current.then(async () => {
      await rfqAPI.saveDraft(state as unknown as Record<string, unknown>);
      setSavedJson(json);
    });
    saveChainRef.current = attempt.catch(() => undefined);
    return attempt;
  }, []);

  const autoSaveEnabled = draftReady && hasUnsavedChanges && !submitting && !submitted;
  const { status, lastSavedAt, flush } = useAutoSave(wizardState, saveDraft, AUTO_SAVE_DELAY_MS, autoSaveEnabled);

  // A failed save would otherwise wait for the next keystroke.
  useEffect(() => {
    if (status !== 'error' || !autoSaveEnabled) return;
    const timer = setTimeout(() => { void flush(); }, RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, autoSaveEnabled, flush]);

  // Refresh / tab close / typed URL while edits are unsaved: let the browser ask.
  useEffect(() => {
    if (!hasUnsavedChanges || submitted) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges, submitted]);

  // Leaving in-app (sidebar, back button) unmounts the wizard with no chance
  // to ask — save on the way out instead. `flush` is stable, so this cleanup
  // runs on unmount only.
  useEffect(() => () => {
    void flush().then((ok) => {
      if (!ok) toast.error('Your latest RFQ changes could not be saved.');
    });
  }, [flush]);

  const resume = useCallback(() => {
    if (!pending) return;
    const hydrated = hydrateWizardDraft(pending.state, openingStateRef.current);
    onHydrate(hydrated);
    // The resumed state is what the server holds — nothing to save until the user edits.
    setSavedJson(JSON.stringify(hydrated));
    setResumedFrom(new Date(pending.updatedAt));
    setPending(null);
    toast.success('Draft resumed');
  }, [pending, onHydrate]);

  const discard = useCallback(async () => {
    try {
      await rfqAPI.deleteDraft();
    } catch (error: unknown) {
      console.warn('[CR-5461] failed to delete RFQ draft', error);
    }
    setPending(null);
  }, []);

  const saveBeforeClose = useCallback(async (): Promise<boolean> => {
    setClosing(true);
    const ok = await flush();
    setClosing(false);
    if (!ok) toast.error('Your latest changes could not be saved. Check your connection, then close again.');
    return ok;
  }, [flush]);

  const clearAfterSubmit = useCallback(async () => {
    setSubmitted(true);
    // A save already on the wire must land before the delete, or it would
    // bring the draft back for an RFQ that now exists.
    await saveChainRef.current;
    try {
      await rfqAPI.deleteDraft();
    } catch (error: unknown) {
      // Best-effort — the RFQ exists; worst case the banner shows once more.
      console.warn('[CR-5461] failed to clear RFQ draft post-submit', error);
    }
  }, []);

  return {
    pendingResume: pending ? { updatedAt: pending.updatedAt } : null,
    resume,
    discard,
    saveStatus: {
      visible: draftReady && !submitting && !submitted,
      status,
      lastSavedAt: lastSavedAt ?? resumedFrom,
      hasUnsavedChanges,
    },
    closing,
    saveBeforeClose,
    clearAfterSubmit,
  };
}
